import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../DataTable/DataTable.css';
import { useMutation, useQuery } from '@tanstack/react-query';
import { message, Button, InputNumber, Table, Space } from 'antd';
import agent from '../../api/agent';

interface CustomerServicePriceRow {
    id?: string;
    productId: string;
    productName: string;
    serviceCostPrice: number | null;
    creditNotePrice: number | null;
    depositPrice: number | null;
}

interface CustomerServicePricesProps {
    customerId: string;
}

const CustomerServicePrices: React.FC<CustomerServicePricesProps> = ({ customerId }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<CustomerServicePriceRow[]>([]);
    const isCustomerReady = !!customerId;

    const { data: pricesData, isLoading: pricesLoading, refetch: refetchPrices } = useQuery({
        queryKey: ['customerServicePrices', customerId],
        queryFn: async () => agent.CustomerServicePrices.getByCustomer(customerId),
        enabled: !!customerId,
    });

    const { data: productsData, isLoading: productsLoading } = useQuery({
        queryKey: ['products'],
        queryFn: async () => agent.Products.getAll(),
    });

    useEffect(() => {
        const productsList = Array.isArray(productsData)
            ? productsData
            : (productsData?.items ?? []);

        if (!productsList) {
            setRows([]);
            return;
        }

        const priceMap = new Map<string, any>();
        if (pricesData) {
            (Array.isArray(pricesData) ? pricesData : pricesData?.items ?? []).forEach((price: any) => {
                const productId = price.product?.id || price.productId;
                if (productId) {
                    priceMap.set(productId, price);
                }
            });
        }

        const nextRows: CustomerServicePriceRow[] = productsList.map((product: any) => {
            const price = priceMap.get(product.id);
            return {
                id: price?.id,
                productId: product.id,
                productName: product.name,
                serviceCostPrice: price?.serviceCostPrice ?? null,
                creditNotePrice: price?.creditNotePrice ?? null,
                depositPrice: price?.depositPrice ?? null,
            };
        });

        setRows(nextRows);
    }, [pricesData, productsData]);

    const { mutate: savePriceHandler } = useMutation({
        mutationFn: async (row: CustomerServicePriceRow) => {
            const normalizeNumber = (value: number | null) =>
                value === null || value === undefined ? null : Number(value);
            const payload = {
                productId: row.productId,
                serviceCostPrice: normalizeNumber(row.serviceCostPrice),
                creditNotePrice: normalizeNumber(row.creditNotePrice),
                depositPrice: normalizeNumber(row.depositPrice),
            };

            if (row.id) {
                return agent.CustomerServicePrices.update(row.id, payload);
            }

            return agent.CustomerServicePrices.create({ customerId, ...payload });
        },
        onSuccess: () => {
            message.success(t('common.updated'));
            refetchPrices();
        },
        onError: (error: any) => {
            message.error(error.message);
        },
    });

    const { mutate: deletePriceHandler } = useMutation({
        mutationFn: async (id: string) => {
            await agent.CustomerServicePrices.delete(id);
        },
        onSuccess: () => {
            message.success(t('common.deleted'));
            refetchPrices();
        },
        onError: (error: any) => {
            message.error(error.message);
        },
    });

    const updateRowValue = (productId: string, field: 'serviceCostPrice' | 'creditNotePrice' | 'depositPrice', value: number | null) => {
        setRows(prev => prev.map(row => row.productId === productId ? { ...row, [field]: value } : row));
    };

    const handleSaveRow = (row: CustomerServicePriceRow) => {
        if (!isCustomerReady) {
            message.error(t('customer.notFound'));
            return;
        }
        const isEmpty = row.serviceCostPrice === null && row.creditNotePrice === null && row.depositPrice === null;
        if (isEmpty && row.id) {
            deletePriceHandler(row.id);
            return;
        }
        if (!isEmpty) {
            savePriceHandler(row);
        }
    };

    const handleClearRow = (row: CustomerServicePriceRow) => {
        if (!isCustomerReady) {
            message.error(t('customer.notFound'));
            return;
        }
        updateRowValue(row.productId, 'serviceCostPrice', null);
        updateRowValue(row.productId, 'creditNotePrice', null);
        updateRowValue(row.productId, 'depositPrice', null);
        if (row.id) {
            deletePriceHandler(row.id);
        }
    };

    const columns = [
        {
            title: t('common.product') || 'Product',
            dataIndex: 'productName',
            key: 'productName',
        },
        {
            title: t('common.serviceCostPrice') || 'Service Cost Price',
            dataIndex: 'serviceCostPrice',
            key: 'serviceCostPrice',
            render: (_: number | null, record: CustomerServicePriceRow) => (
                <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    value={record.serviceCostPrice}
                    placeholder="-"
                    onChange={(value) => updateRowValue(record.productId, 'serviceCostPrice', value ?? null)}
                />
            ),
        },
        {
            title: t('common.creditNotePrice') || 'Credit Note Price',
            dataIndex: 'creditNotePrice',
            key: 'creditNotePrice',
            render: (_: number | null, record: CustomerServicePriceRow) => (
                <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    value={record.creditNotePrice}
                    placeholder="-"
                    onChange={(value) => updateRowValue(record.productId, 'creditNotePrice', value ?? null)}
                />
            ),
        },
        {
            title: t('common.depositPrice') || 'Deposit Price',
            dataIndex: 'depositPrice',
            key: 'depositPrice',
            render: (_: number | null, record: CustomerServicePriceRow) => (
                <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    value={record.depositPrice}
                    placeholder="-"
                    onChange={(value) => updateRowValue(record.productId, 'depositPrice', value ?? null)}
                />
            ),
        },
        {
            title: t('common.actions') || 'Actions',
            key: 'actions',
            render: (_: any, record: CustomerServicePriceRow) => (
                <Space>
                    <Button type="primary" size="small" onClick={() => handleSaveRow(record)} disabled={!isCustomerReady}>
                        {t('common.save') || 'Save'}
                    </Button>
                    <Button danger size="small" onClick={() => handleClearRow(record)} disabled={!isCustomerReady}>
                        {t('common.clear') || 'Clear'}
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: '20px' }}>
            <Table
                columns={columns}
                dataSource={rows}
                loading={pricesLoading || productsLoading}
                rowKey="productId"
                pagination={{ pageSize: 10 }}
            />
        </div>
    );
};

export default CustomerServicePrices;
